import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/accessibility";
import "./highcharts-responsive.css";
import { CardContent } from "@/components/ui/card";
import { useDashboardData } from "@/services/dashboardDataService";
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import { cleanDateAndTimeObject } from "@shared/renderDate";

// Colors for the chart
const CHART_COLORS = {
  onTime: "#4CAF50",
  overdue: "#F66A6A",
  empty: "#D3D3D3"
};

const getChartOptions = (onTimePercentage: number, overduePercentage: number): Highcharts.Options => ({
  chart: {
    type: "pie",
    backgroundColor: "transparent",
    spacing: [0, 0, 0, 0],
    height: '100%',
  },
  title: {
    text: undefined,
  },
  tooltip: {
    pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
  },
  exporting: {
    enabled: false,
  },
  credits: {
    enabled: false
  },
  plotOptions: {
    pie: {
      innerSize: "65%",
      borderWidth: 0,
      dataLabels: { 
        enabled: false 
      },
      size: "100%",
      showInLegend: true
    },
  },
  legend: {
    enabled: false
  },
  series: [
    {
      type: "pie",
      name: "Assurance Health",
      data: [
        { 
          name: "On Time", 
          y: onTimePercentage, 
          color: CHART_COLORS.onTime 
        },
        { 
          name: "Overdue", 
          y: overduePercentage, 
          color: CHART_COLORS.overdue 
        }
      ],
    },
  ],
});

const AssuranceHealthWidget: React.FC = () => {
  // Refs
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State for resize handling
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Fetch data from the assurance health collector
  const { data, isLoading, error, metadata } = useDashboardData('assurance-health');
  
  // Report widget status to dashboard context
  useWidgetStatusReporter('assurance-health', isLoading, error, !!data);
  
  // Calculate derived data
  const onTimePercentage = data?.onTimePercentage ?? 0;
  const overduePercentage = data?.overduePercentage ?? 0;
  const chartOptions = getChartOptions(onTimePercentage, overduePercentage);
  const lastUpdated = data?.lastUpdated?.toDate ? new Date(data.lastUpdated.toDate()).toLocaleTimeString() : null;
  
  // Handle resize events
  useEffect(() => {
    if (!containerRef.current) return;
    
    const handleResize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
      
      if (chartRef.current?.chart?.reflow) {
        chartRef.current.chart.reflow();
        // Small delay to ensure proper rendering
        const timer = setTimeout(() => {
          chartRef.current?.chart?.reflow();
        }, 100);
        return () => clearTimeout(timer);
      }
    };
    
    // Set initial size
    handleResize();
    
    // Set up resize observer
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);
    
    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []); // Empty dependency array means this effect runs once on mount
  
  // Render loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full p-0 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading assurance health data...</p>
        </div>
      </CardContent>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <CardContent className="h-full w-full p-4 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-medium">Error loading assurance health data</p>
          <p className="text-sm">{error}</p>
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full p-0 flex flex-col items-center justify-center">
      <div className="w-full flex justify-between items-center p-2">
        <h3 className="text-lg font-semibold">Assurance Health</h3>
        {metadata?.lastUpdated && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {cleanDateAndTimeObject(metadata.lastUpdated).toLocaleTimeString()}
          </span>
        )}
      </div>
      <div
        ref={containerRef}
        className="h-full w-full flex items-center justify-center p-0"
        style={{ minHeight: 0, minWidth: 0 }}
      >
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartRef}
          containerProps={{ style: { width: '100%', height: '100%' } }}
        />
      </div>
      <div className="w-full flex justify-center gap-4 p-2 text-sm">
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ backgroundColor: CHART_COLORS.onTime }}></span>
          On Time: {onTimePercentage}%
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ backgroundColor: CHART_COLORS.overdue }}></span>
          Overdue: {overduePercentage}%
        </div>
      </div>
    </CardContent>
  );
};

export default AssuranceHealthWidget;
